import './TeamMembers.css'
import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { Box, Button, Card, IconButton, TextField, useTheme } from "@mui/material"
import { useGridApiRef } from "@mui/x-data-grid"
import PageHeader from "../../../components/PageHeader"
import PersonIcon from "@mui/icons-material/Person"
import { selectGroups, selectJobTitles, selectIsCorporateParent } from "../../../redux/customer"
import { selectIsChildCorporateParent } from '../../../redux/childCustomer'
import { useAppSelector } from "../../../redux/store"
import { autoSizeGrid, getFilteredTeamMembers } from "./utils/helpers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ToggleActiveButtons from "../../../components/ToggleActiveButtons"
import debounce from "../../../helpers/debounce"
import { LoadingState, TeamMember } from "../../../types"
import TeamMemberModal from "./TeamMemberModal"
import { OpenTeamMemberModal } from "./types"
import TeamMembersTable from './TeamMembersTable'
import useCustomerNumber from '../../../hooks/useCustomerNumber'
import useCustomerLoading from '../../../hooks/useCustomerLoading'
import useIsCorpView from '../../../hooks/useIsCorpView'
import useTeamMembers from '../../../hooks/useTeamMembers'

const TeamMembers = () => {
  const theme = useTheme()
  const apiRef = useGridApiRef()

  const loadingState = useCustomerLoading()

  const [search, setSearch] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [openModal, setOpenModal] = useState<OpenTeamMemberModal>({ open: false, teamMember: null })

  const customerNumber = useCustomerNumber()

  const isCorpPracticeView = useIsCorpView()

  const isCorporateParent = isCorpPracticeView
    ? useAppSelector(selectIsChildCorporateParent)
    : useAppSelector(selectIsCorporateParent)

  const teamMembers = useTeamMembers()

  const groups = useAppSelector(selectGroups)

  const jobTitles = useAppSelector(selectJobTitles)

  const isLoading = loadingState === LoadingState.Loading

  const teamMembersByStatus = useMemo(() => {
    const membersByStatus = new Map<string, TeamMember[]>([
      ['active', []],
      ['inactive', []],
    ])

    teamMembers.forEach(teamMember => {
      if (teamMember.account_active && teamMember.verified) membersByStatus.get('active')?.push(teamMember)
      else if (!teamMember.account_active) membersByStatus.get('inactive')?.push(teamMember)
    })

    return membersByStatus
  }, [teamMembers])

  const pendingTeamMembers = useMemo(() => {
    return teamMembers.filter(teamMember => teamMember.account_active && !teamMember.verified)
  }, [teamMembers])

  const filteredTeamMembers = useMemo(() => {
    const status = isActive ? 'active' : 'inactive'
    const members = teamMembersByStatus.get(status) ?? []

    return teamMembers?.length
      ? getFilteredTeamMembers(search, members)
      : []
  }, [isActive, search, teamMembers])

  const handleTeamMember = () => {
    setOpenModal({ open: true, teamMember: null })
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const searchValue = event.target.value
    setSearch(searchValue)
  }

  const debouncedHandleSearch = debounce(handleSearch, 50)

  const autosizeOptions = {
    columns: ['groups'],
    includeHeaders: true,
    includeOutliers: true,
    outliersFactor: 1.5,
  }

  const noTeamMembers = Boolean(teamMembers?.length === 0) && loadingState === LoadingState.Done

  useEffect(() => {
    if (loadingState === LoadingState.Done) {
      autoSizeGrid(apiRef, autosizeOptions)
    }
  }, [loadingState, apiRef])

  return (
    <>
      <PageHeader MuiIcon={PersonIcon} headerName="Team Members" />
      {pendingTeamMembers.length > 0 && (
        <Box sx={{ padding: '12px 12px 0 12px' }}>
          <Card sx={{ width: "100%" }} variant="outlined">
            <Box className="box-header">Pending Team Members</Box>
            <Box>
              <TeamMembersTable
                apiRef={apiRef}
                teamMembers={pendingTeamMembers}
                isLoading={isLoading}
                autosizeOptions={autosizeOptions}
                setOpenModal={setOpenModal}
                pending={true}
              />
            </Box>
          </Card>
        </Box>
      )}
      <Box sx={{ padding: '12px' }}>
        <Card sx={{ width: "100%" }} variant="outlined">
          <Box className="box-header">
            {isActive ? 'Active' : 'Inactive'} Team Members
          </Box>
          <Box className="table-subheader flex flex-space-between">
            <Box>
              <TextField
                sx={{ minWidth: 200, '& .MuiInputBase-root': { paddingRight: '4px' } }}
                size="small"
                label="Search"
                onChange={debouncedHandleSearch}
                InputProps={{
                  endAdornment: (
                    <IconButton>
                      <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
                    </IconButton>
                  )
                }}
              />
            </Box>
            <Box>
              <ToggleActiveButtons isActive={isActive} setIsActive={setIsActive} />
            </Box>
          </Box>
          <Box className="table-subheader flex flex-end">
            <Button
              variant='contained'
              onClick={() => handleTeamMember()}
              disabled={!isActive}
            >
              Add Team Member
            </Button>
          </Box>
          <Box>
            {
              !noTeamMembers && (
                <TeamMembersTable
                  apiRef={apiRef}
                  teamMembers={filteredTeamMembers}
                  isLoading={isLoading}
                  autosizeOptions={autosizeOptions}
                  setOpenModal={setOpenModal}
                />
              )
            }
            {
              noTeamMembers && (
                <Box sx={{ padding: '16px', textAlign: 'center' }}>
                  <Box sx={{ fontWeight: theme.typography.fontWeightBold, marginBottom: '16px' }}>
                    This practice does not currently have any team members associated with it.
                  </Box>
                  <Box sx={{ fontWeight: theme.typography.fontWeightBold, marginBottom: '16px' }}>
                    Click on 'Add Team Member' to get started.
                  </Box>
                  <Box sx={{ fontWeight: theme.typography.fontWeightBold, marginBottom: '16px' }}>
                    Please note the first team member added to this practice will be considered the 'owner' of the account.
                  </Box>
                  <Box sx={{ fontWeight: theme.typography.fontWeightBold, marginBottom: '16px' }}>
                    The owner of the account will be able to edit tasks created and assigned by any user in the practice.
                  </Box>
                </Box>
              )
            }
          </Box>
        </Card>
      </Box>
      {
        openModal.open && teamMembers && jobTitles
        && (
          <TeamMemberModal
            customerNumber={customerNumber}
            openModal={openModal}
            setOpenModal={setOpenModal}
            groups={isCorporateParent ? groups : null}
            jobTitles={jobTitles}
            teamMembers={teamMembers}
          />
        )
      }
    </>
  )
}

export default TeamMembers
